// React imports
import React, { useEffect, useRef, useState } from 'react';

// Router imports
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

// AWS Amplify imports
import { fetchAuthSession, signOut } from 'aws-amplify/auth';

// Redux imports
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { clearUser } from '../../redux/userSlice';
import { clearUserTypes } from '../../redux/userTypesSlice';
import { clearSupports } from '../../redux/supportSlice';
import { clearDepartments } from '../../redux/departmentsSlice';
import { clearSchools } from '../../redux/schoolsSlice';
import { clearAllIPStatuses } from '../../redux/ipStatusesSlice';

// Utility imports
import { toast } from 'react-toastify';

// Constant imports
import {
  CHECK_SESSION_EXPIRATION_TIME,
  ID_TOKEN_EXPIRATION_TIME_IN_SECONDS,
  REFRESH_TOKEN_EXPIRATION_TIME_IN_SECONDS,
} from '../../constants/Constants';

// Component imports
import Header from './Header';
import Footer from './Footer';
import PrimaryNavigation from './PrimaryNavigation';
import GenericConfirmationModal from './GenericConfirmationModal';

// Style imports
import '../../styles/Layout.css';
import '@fontsource/inter/100.css'; // Thin
import '@fontsource/inter/200.css'; // Extra Light
import '@fontsource/inter/300.css'; // Light
import '@fontsource/inter/400.css'; // Regular
import '@fontsource/inter/500.css'; // Medium
import '@fontsource/inter/600.css'; // Semi Bold
import '@fontsource/inter/700.css'; // Bold
import '@fontsource/inter/800.css'; // Extra Bold
import '@fontsource/inter/900.css'; // Black
import '../../styles/CustomStyles.css';

// Custom Hook imports
import useScreenSize from '../../hooks/useScreenSize';

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.user);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isMobile = useScreenSize('(max-width: 576px)');

  const handleDrawerToggle = () => {
    setIsDrawerOpen((prevState) => !prevState);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  useEffect(() => {
    if (!isMobile) {
      setIsDrawerOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!user.userDetails) {
      setIsDrawerOpen(false);
    }
  }, [user.userDetails]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    if (isDrawerOpen || isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isDrawerOpen, isModalOpen]);

  const isRememberSessionRef = useRef(user.userDetails?.isRememberSession);

  useEffect(() => {
    isRememberSessionRef.current = user.userDetails?.isRememberSession;
  }, [user.userDetails?.isRememberSession]);

  const isRefreshTokenValidFor30Days = async (
    token: string | undefined,
  ): Promise<boolean> => {
    if (!token) return false;
    try {
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const validTimeThreshold =
        decodedToken.auth_time + REFRESH_TOKEN_EXPIRATION_TIME_IN_SECONDS;
      return currentTime <= validTimeThreshold;
    } catch (error) {
      console.error(
        'checkTokenValidity30Days: Error checking validity token:',
        error,
      );
      return false;
    }
  };

  const isIdTokenValid = async (
    token: string | undefined,
  ): Promise<boolean> => {
    if (!token) return false;
    try {
      const iatTimeinSeconds =
        JSON.parse(atob(token.split('.')[1])).iat -
        CHECK_SESSION_EXPIRATION_TIME / 1000;
      CHECK_SESSION_EXPIRATION_TIME / 10;
      const currentTime = Math.floor(Date.now() / 1000);
      const validTimeThreshold =
        iatTimeinSeconds + ID_TOKEN_EXPIRATION_TIME_IN_SECONDS;
      const isRefreshTokenValid = await isRefreshTokenValidFor30Days(token);
      if (!isRefreshTokenValid) {
        await handleLogout();
      }
      return validTimeThreshold <= currentTime;
    } catch (error) {
      console.error(
        'checkTokenValidity: Error checking validity token:',
        error,
      );
      return false;
    }
  };

  const checkExpiry = async () => {
    try {
      const tokens = (await fetchAuthSession()).tokens;
      if (tokens?.idToken) {
        if (isRememberSessionRef.current) {
          const value = await isRefreshTokenValidFor30Days(
            tokens?.idToken?.toString(),
          );
          if (!value) {
            await handleLogout();
          }
        } else {
          const value = await isIdTokenValid(tokens?.idToken?.toString());
          if (value) {
            setIsModalOpen(true);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching auth session or processing tokens:', error);
      // Optionally, handle session expiry or logout here
      await handleLogout();
    }
  };

  useEffect(() => {
    const fetchTokensAndSetInterval = async () => {
      try {
        const tokens = (await fetchAuthSession()).tokens;
        if (tokens?.idToken) {
          const intervalId = setInterval(
            () => checkExpiry(),
            CHECK_SESSION_EXPIRATION_TIME,
          );

          return intervalId;
        } else {
          if (user.userDetails !== null) {
            await handleLogout();
          }
        }
      } catch (error) {
        console.error('Error fetching tokens or setting interval:', error);
        // Optionally handle the error, maybe force a logout or show an error message
        if (user.userDetails !== null) {
          await handleLogout();
        }
      }
    };

    const intervalIdPromise = fetchTokensAndSetInterval();

    return () => {
      intervalIdPromise.then((id) => {
        clearInterval(id);
      });
    };
  }, [user.userDetails]);

  const handleLogout = async () => {
    try {
      // Sign out from AWS Cognito
      await signOut();
      dispatch(clearUser());
      dispatch(clearUserTypes());
      dispatch(clearSupports());
      dispatch(clearDepartments());
      dispatch(clearSchools());
      dispatch(clearAllIPStatuses());
      setIsModalOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Error during logout process:', error);
      toast.error('Logout failed, please try again.');
    }
  };

  const handleContinue = async () => {
    setIsModalOpen(false);
  };

  return (
    <div className="layout-container">
      <header className="root-header">
        <Header onMenuClick={handleDrawerToggle} />
      </header>
      <main className="layout-content custom-margin col-lg-8 col-md-12 col-sm-12">
        {/* <div className="common-background"> */}
        <Outlet />
        {/* </div> */}
      </main>
      <footer className="root-footer row justify-content-center p-0 g-0">
        <Footer />
      </footer>

      {user.userDetails && isDrawerOpen && (
        <PrimaryNavigation isOpen={isDrawerOpen} onClose={handleDrawerClose} />
      )}

      <GenericConfirmationModal
        message="Are you sure you want to log out?"
        isOpen={isModalOpen}
        onSecondButtonAction={handleLogout}
        onSecondButtonName="Yes, Logout"
        onFirstButtonAction={handleContinue}
        onFirstButtonName="Continue"
      />
    </div>
  );
};

export default Layout;
